import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useSnapshot } from 'valtio';

import {
  ACTOR_ACCENT_BG_CLASSES,
  Player,
  type PlayerCameraControlsHandle,
  usePlayerActions,
  usePlayerActorScene,
  usePlayerStore,
} from '@cartwhl/3d/player';
import Loading from '@cartwhl/ui/atoms/Loading';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import HomeIcon from '@cartwhl/ui/icons/HomeIcon';
import Button from '@cartwhl/ui/input/Button';
import Overlay from '@cartwhl/ui/player/Overlay';

import * as exportsStore from 'stores/exportsStore';

import { CanvasLayoutHeaderContent } from '../../CanvasLayout';
import {
  CAMERA_DISTANCE_MULTIPLIER,
  CHARACTER_ROTATION,
  DEFAULT_CAMERA_POSITION,
  DEFAULT_FALLBACK_BVH_URLS,
  getMotionEditorCharacterRotation,
} from '../constants';
import {
  ENABLE_FRONT_DOOR,
  ENABLE_MOTION_EDITOR_ADVANCED_GHOST_OPTIONS,
  ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES,
  isMotionEditorPanelEnabled,
} from '../feature-flags';
import {
  type MotionEditorBonePick,
  MotionEditorLiveBoneHoverPicker,
} from '../keyPoses/bonePicking';
import HistoryBvhGhost from '../keyPoses/components/HistoryBvhGhost';
import KeyPoseEditing from '../keyPoses/components/KeyPoseEditing';
import KeyPoseGhosts from '../keyPoses/components/KeyPoseGhosts';
import KeyPoseHotkeysBinding from '../keyPoses/components/KeyPoseHotkeysBinding';
import UnsupportedCharacterKeyPoseModal from '../keyPoses/components/UnsupportedCharacterKeyPoseModal';
import { useCurrentFrameKeyPoseRenderState } from '../keyPoses/hooks/useCurrentFrameKeyPoseRenderState';
import { useKeyPoseGenerationCameraPreserve } from '../keyPoses/hooks/useKeyPoseGenerationCameraPreserve';
import { useKeyPoseGenerationRecovery } from '../keyPoses/hooks/useKeyPoseGenerationRecovery';
import { useKeyPoseSceneRecovery } from '../keyPoses/hooks/useKeyPoseSceneRecovery';
import { useLongSourceMotionEditingWarning } from '../keyPoses/hooks/useLongSourceMotionEditingWarning';
import {
  getKeyPoseFkJointIdsForIkEffector,
  resolveKeyPoseIkEffectorFromFkJoint,
} from '../keyPoses/ik/keyPoseIkConfig';
import {
  resolveActiveKeyPoseReferenceName,
  resolveSelectedKeyPoseMotionForReference,
} from '../keyPoses/keyPoseMotionUtils';
import { useStore as useKeyPoseStore } from '../keyPoses/keyPoseStore';
import type { KeyPoseFkJointId } from '../keyPoses/types';
import { useStore } from '../motionEditorStore';
import {
  EditControlsOverlay,
  RepathingHotkeysBinding,
  RepathingSceneLayer,
} from '../repathing';
import { useStore as useRepathingStore } from '../repathing/repathingStore';
import {
  type MotionEditorSceneActorRenderItem,
  selectMotionEditorActorTargets,
  selectSceneActorRenderItems,
  selectSelectedCharacterMeta,
} from '../store/motionEditorSelectors';
import type { MotionEditorActor } from '../types';
import {
  MotionEditorWorldObjectControls,
  MotionEditorWorldObjectLayer,
  useMotionEditorWorldObjects,
} from '../worldObjects';
import EditableSceneName from './EditableSceneName';
import Footer from './Footer';
import MotionEditorFrontDoor, {
  type MotionEditorFrontDoorMode,
} from './MotionEditorFrontDoor';
import MotionEditorLoader from './MotionEditorLoader';
import MotionEditorVideoRecorderBridge from './MotionEditorVideoRecorderBridge';
import SceneDownloadModal from './SceneDownloadModal';
import SidePanels, {
  MotionEditorPanel,
  MotionEditorPanelButtons,
} from './SidePanels';
import { useScenePlaybackSettingsSync } from './useScenePlaybackSettingsSync';

const ACTOR_ACCENT_GHOST_COLORS = {
  'bg-blue': '#1478ff',
  'bg-red': '#ff3f56',
  'bg-purple': '#5900f5',
  'bg-orange': '#e77b0e',
} as const satisfies Record<(typeof ACTOR_ACCENT_BG_CLASSES)[number], string>;

export default function MotionEditor() {
  const { sceneID: routeSceneID } = useParams<{ sceneID: string }>();
  const [searchParams] = useSearchParams();
  const routeMotionValue = searchParams.get('motion');
  const hasRouteSceneID = Boolean(routeSceneID);
  const store = useStore();
  const repathingStore = useRepathingStore();
  const exportsSnapshot = useSnapshot(exportsStore.state);
  const { scene, characters, playback } = store.state;
  const { selectedCharacter, characterSettings } = characters;
  const { sceneActors, selectedReferenceName } = playback;
  const isMultiActorScene = hasRouteSceneID && sceneActors.length > 1;
  const selectedCharacterMeta = selectSelectedCharacterMeta(store.state);
  const { activePlayerActorId, cameraFitActorId, cameraFitKey } =
    selectMotionEditorActorTargets(store.state);
  const sceneActorRenderItems = selectSceneActorRenderItems(store.state);
  const stableSceneActorIds = useMemo(
    () => sceneActors.map((actor) => actor.referenceName),
    [sceneActors],
  );

  useEffect(() => {
    if (!routeSceneID) return;
    void store.loadScene(routeSceneID, routeMotionValue);
  }, [routeMotionValue, routeSceneID, store]);

  const cameraControlsRef = useRef<PlayerCameraControlsHandle | null>(null);
  const activeActorScene = usePlayerActorScene(cameraFitActorId);
  const playerActions = usePlayerActions();
  const playbackSettingsActorId =
    playback.selectedSceneMotion?.referenceName ??
    selectedReferenceName ??
    (sceneActors.length === 1
      ? sceneActors[0]?.referenceName
      : activePlayerActorId);
  const playbackSettingsMotion = playbackSettingsActorId
    ? resolveSelectedKeyPoseMotionForReference(
        playbackSettingsActorId,
        selectedReferenceName,
        playback.selectedSceneMotion,
        sceneActors,
      )
    : null;
  useScenePlaybackSettingsSync({
    actorId: playbackSettingsActorId,
    hasRouteSceneID,
    scene,
    timelineIndex: playbackSettingsMotion?.timelineIndex,
  });
  useKeyPoseSceneRecovery();
  useKeyPoseGenerationRecovery();
  useKeyPoseGenerationCameraPreserve();
  useLongSourceMotionEditingWarning();

  const [openPanel, setOpenPanel] = useState<MotionEditorPanel | null>(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [frontDoorMode, setFrontDoorMode] =
    useState<MotionEditorFrontDoorMode>('');
  const stageRef = useRef<HTMLDivElement | null>(null);
  const worldObjects = useMotionEditorWorldObjects({ stageRef });

  useEffect(() => {
    if (openPanel && !isMotionEditorPanelEnabled(openPanel)) {
      setOpenPanel(null);
    }
  }, [openPanel]);

  const handleActorNameTagClick = useCallback(
    (actorId: string) => {
      if (!isMultiActorScene) return;
      store.selectReference(actorId);
    },
    [isMultiActorScene, store],
  );
  const handleSelectReference = useCallback(
    (referenceName: string) => {
      store.selectReference(referenceName);
    },
    [store],
  );
  const handleDeselectReference = useCallback(() => {
    store.deselectReference();
  }, [store]);
  useEffect(() => {
    playerActions.setActiveActor(selectedReferenceName);
  }, [playerActions, selectedReferenceName]);
  const showReferencePicker = isMultiActorScene;
  const sceneExportJob = scene.data
    ? exportsSnapshot.sceneExports[scene.data.sceneID]
    : undefined;
  const isSceneExportActive =
    exportsStore.isSceneExportInProgress(sceneExportJob);

  const defaultRotation = getMotionEditorCharacterRotation(
    characterSettings.isLegacyCharacter,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <CanvasLayoutHeaderContent slot="start">
        <Link to="/" aria-label="All applets">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={<HomeIcon />}
            tabIndex={-1}
            aria-hidden
          />
        </Link>
      </CanvasLayoutHeaderContent>
      {scene.data && (
        <>
          <CanvasLayoutHeaderContent slot="center">
            <EditableSceneName
              sceneID={scene.data.sceneID}
              sceneName={scene.data.sceneName}
              onPickNewMotion={
                ENABLE_FRONT_DOOR
                  ? () => setFrontDoorMode('motions')
                  : undefined
              }
            />
          </CanvasLayoutHeaderContent>
          <CanvasLayoutHeaderContent slot="end">
            <Button
              variant="primary"
              size="sm"
              icon={
                isSceneExportActive ? (
                  <Loading className="h-4 w-4" />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={() => setDownloadModalOpen(true)}
            >
              Download
            </Button>
          </CanvasLayoutHeaderContent>
        </>
      )}
      <div ref={stageRef} className="relative min-h-0 w-full flex-1">
        <div className="absolute inset-0 overflow-hidden rounded-3xl lg:flex lg:justify-center">
          <div className="h-full w-full lg:w-[calc(100vw-48px)] lg:shrink-0">
            <Player.Canvas onActorNameTagClick={handleActorNameTagClick}>
              {ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES ? (
                repathingStore.state.controlsMode === 'body' ? (
                  <KeyPoseHotkeysBinding
                    cameraControlsRef={cameraControlsRef}
                  />
                ) : (
                  <RepathingHotkeysBinding />
                )
              ) : null}
              <MotionEditorVideoRecorderBridge />
              <Player.Environment />
              <Player.DefaultCamera
                position={DEFAULT_CAMERA_POSITION}
                zoom={2}
              />
              <Player.CameraControls
                cameraControlsRef={cameraControlsRef}
                fitCharacter
                fitCharacterTarget={activeActorScene}
                fitCharacterKey={cameraFitKey}
                fitCharacterDistanceMultiplier={CAMERA_DISTANCE_MULTIPLIER}
                relockTargetOnPlaybackStop={false}
              />
              <MotionEditorWorldObjectLayer {...worldObjects.layerProps} />
              {!hasRouteSceneID ? (
                selectedCharacter && (
                  <Player.FallbackCharacter
                    actorId={activePlayerActorId}
                    characterID={selectedCharacter.characterID}
                    meshPath={selectedCharacter.meshPath}
                    meshFormat={selectedCharacter.meshFormat}
                    rotationOffsets={selectedCharacter.rotationOffsets}
                    skinHex={characterSettings.skinHex}
                    faceExpression={characterSettings.faceExpression}
                    bvhUrls={DEFAULT_FALLBACK_BVH_URLS}
                    rotation={defaultRotation}
                    shuffle
                    headBoneName={selectedCharacter.headBoneName}
                  />
                )
              ) : (
                <MotionEditorRouteSceneStage
                  actorIds={stableSceneActorIds}
                  isMultiActorScene={isMultiActorScene}
                  onDeselectReference={handleDeselectReference}
                  onSelectReference={handleSelectReference}
                  sceneActors={sceneActors}
                  sceneActorRenderItems={sceneActorRenderItems}
                  selectedReferenceName={selectedReferenceName}
                />
              )}
            </Player.Canvas>
          </div>
        </div>

        <SidePanels openPanel={openPanel} onOpenPanelChange={setOpenPanel} />

        <Overlay.Root>
          <Player.CameraControlsOverlay cameraControlsRef={cameraControlsRef} />
          {ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES && scene.data ? (
            <EditControlsOverlay />
          ) : null}
          <MotionEditorPanelButtons
            placement="bottom-right"
            openPanel={openPanel}
            onOpenPanelChange={setOpenPanel}
            selectedCharacterMeta={selectedCharacterMeta}
          />
        </Overlay.Root>

        <MotionEditorWorldObjectControls {...worldObjects.controlsProps} />

        {ENABLE_FRONT_DOOR && (
          <div className="pointer-events-none absolute inset-4 inset-y-4 z-10 flex justify-center lg:inset-x-40">
            <MotionEditorFrontDoor
              routeSceneID={routeSceneID}
              frontDoorMode={frontDoorMode}
              onFrontDoorModeChange={setFrontDoorMode}
            />
          </div>
        )}
      </div>

      <Footer
        hasMotionToPlay={Boolean(scene.data)}
        showNameTagToggle={isMultiActorScene}
        showReferencePicker={showReferencePicker}
        sceneReferences={sceneActors}
        selectedReferenceName={selectedReferenceName}
        onSelectReference={handleSelectReference}
      />

      <MotionEditorLoader />
      {downloadModalOpen && scene.data ? (
        <SceneDownloadModal
          scene={scene.data}
          close={() => setDownloadModalOpen(false)}
        />
      ) : null}
      <UnsupportedCharacterKeyPoseModal />
    </div>
  );
}

interface MotionEditorRouteSceneStageProps {
  actorIds: readonly string[];
  isMultiActorScene: boolean;
  onDeselectReference: () => void;
  onSelectReference: (referenceName: string) => void;
  sceneActors: readonly MotionEditorActor[];
  sceneActorRenderItems: readonly MotionEditorSceneActorRenderItem[];
  selectedReferenceName: string | null;
}

function MotionEditorRouteSceneStage({
  actorIds,
  isMultiActorScene,
  onDeselectReference,
  onSelectReference,
  sceneActors,
  sceneActorRenderItems,
  selectedReferenceName,
}: MotionEditorRouteSceneStageProps) {
  const keyPoseStore = useKeyPoseStore();
  const repathingStore = useRepathingStore();
  const playerActions = usePlayerActions();
  const playerSnapshot = usePlayerStore();
  const isPlaying = playerSnapshot.state.transport.isPlaying;
  const keyPoseEditingEnabled =
    ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES &&
    repathingStore.state.controlsMode === 'body';
  const repathingEditingEnabled =
    ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES &&
    repathingStore.state.controlsMode === 'path';
  const currentFrameKeyPoseRenderState = useCurrentFrameKeyPoseRenderState({
    sceneRotation: CHARACTER_ROTATION,
  });
  const editableKeyPoseReferenceName = keyPoseEditingEnabled
    ? currentFrameKeyPoseRenderState.editableReferenceName
    : null;
  const liveActorHiddenReferenceName = keyPoseEditingEnabled
    ? currentFrameKeyPoseRenderState.liveActorHiddenReferenceName
    : null;
  const activeKeyPoseReferenceName = keyPoseEditingEnabled
    ? resolveActiveKeyPoseReferenceName(
        keyPoseStore.state.activeReferenceName,
        selectedReferenceName,
        sceneActors,
      )
    : null;
  const ghostActorIds = useMemo(() => {
    if (
      !keyPoseStore.state.ghostOptions.showOnlyCurrentActor ||
      !activeKeyPoseReferenceName ||
      !actorIds.includes(activeKeyPoseReferenceName)
    ) {
      return actorIds;
    }

    return [activeKeyPoseReferenceName];
  }, [
    activeKeyPoseReferenceName,
    actorIds,
    keyPoseStore.state.ghostOptions.showOnlyCurrentActor,
  ]);
  const actorGhostColors = useMemo(() => {
    if (
      !ENABLE_MOTION_EDITOR_ADVANCED_GHOST_OPTIONS ||
      !keyPoseStore.state.ghostOptions.useActorColors
    ) {
      return undefined;
    }

    return Object.fromEntries(
      actorIds.map((actorId, index) => {
        const accent =
          ACTOR_ACCENT_BG_CLASSES[index % ACTOR_ACCENT_BG_CLASSES.length]!;
        return [actorId, ACTOR_ACCENT_GHOST_COLORS[accent]];
      }),
    );
  }, [actorIds, keyPoseStore.state.ghostOptions.useActorColors]);
  const keyPoseEditing = keyPoseStore.state.editing;
  const hoveredBonePickerJointIds = useMemo(
    () =>
      keyPoseEditingEnabled
        ? keyPoseEditing.mode === 'ik'
          ? getKeyPoseFkJointIdsForIkEffector(
              keyPoseEditing.hoveredIkEffectorId,
            )
          : getBonePickerJointIds(keyPoseEditing.hoveredFkJointId)
        : [],
    [
      keyPoseEditing.hoveredFkJointId,
      keyPoseEditing.hoveredIkEffectorId,
      keyPoseEditing.mode,
      keyPoseEditingEnabled,
    ],
  );
  const selectedBonePickerJointIds = useMemo(
    () =>
      keyPoseEditingEnabled
        ? keyPoseEditing.mode === 'ik'
          ? getKeyPoseFkJointIdsForIkEffector(
              keyPoseEditing.selectedIkEffectorId,
            )
          : getBonePickerJointIds(keyPoseEditing.selectedFkJointId)
        : [],
    [
      keyPoseEditing.mode,
      keyPoseEditing.selectedFkJointId,
      keyPoseEditing.selectedIkEffectorId,
      keyPoseEditingEnabled,
    ],
  );
  const bonePickerRaycastEnabled = !keyPoseEditing.dragging;

  const handleBoneHover = useCallback(
    (pick: MotionEditorBonePick | null) => {
      const editing = keyPoseStore.state.editing;
      const nextJointId = pick?.jointId ?? null;

      if (editing.mode === 'ik') {
        const nextEffectorId = resolveKeyPoseIkEffectorFromFkJoint(nextJointId);
        if (editing.hoveredFkJointId !== nextJointId) {
          keyPoseStore.setKeyPoseFkHoveredJoint(nextJointId);
        }
        if (editing.hoveredIkEffectorId !== nextEffectorId) {
          keyPoseStore.setKeyPoseIkHoveredEffector(nextEffectorId);
        }
        return;
      }

      if (editing.hoveredFkJointId === nextJointId) return;
      keyPoseStore.setKeyPoseFkHoveredJoint(nextJointId);
    },
    [keyPoseStore],
  );
  const handleBoneSelect = useCallback(
    (pick: MotionEditorBonePick) => {
      const { dragging, mode } = keyPoseStore.state.editing;
      if (dragging) {
        return false;
      }

      if (mode === 'ik') {
        const effectorId = resolveKeyPoseIkEffectorFromFkJoint(pick.jointId);
        if (!effectorId) return false;

        playerActions.pause();
        keyPoseStore.setKeyPoseIkHoveredEffector(effectorId);
        keyPoseStore.setKeyPoseIkSelectedEffector(effectorId);
        return true;
      }

      playerActions.pause();
      keyPoseStore.setKeyPoseFkHoveredJoint(pick.jointId);
      keyPoseStore.setKeyPoseFkSelectedJoint(pick.jointId);
      return true;
    },
    [keyPoseStore, playerActions],
  );

  return (
    <>
      {keyPoseEditingEnabled ? (
        <>
          <KeyPoseGhosts
            actorIds={ghostActorIds}
            actorGhostColors={actorGhostColors}
            editableKeyPoseId={currentFrameKeyPoseRenderState.editableKeyPoseId}
            editableReferenceName={editableKeyPoseReferenceName}
            hoveredJointIds={hoveredBonePickerJointIds}
            liveActorReplacementKeyPoseId={
              currentFrameKeyPoseRenderState.liveActorReplacementKeyPoseId
            }
            onBoneHover={handleBoneHover}
            onBoneSelect={handleBoneSelect}
            raycastEnabled={bonePickerRaycastEnabled}
            sceneRotation={CHARACTER_ROTATION}
            selectedJointIds={selectedBonePickerJointIds}
          />
          {currentFrameKeyPoseRenderState.showKeyPoseGizmos ? (
            <KeyPoseEditing sceneRotation={CHARACTER_ROTATION} />
          ) : null}
        </>
      ) : null}
      {ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES ? (
        <HistoryBvhGhost sceneRotation={CHARACTER_ROTATION} />
      ) : null}
      {repathingEditingEnabled ? <RepathingSceneLayer /> : null}
      {sceneActorRenderItems.map((renderItem) => (
        <MotionEditorRouteSceneActor
          key={renderItem.actor.referenceName}
          activeKeyPoseReferenceName={activeKeyPoseReferenceName}
          bonePickerRaycastEnabled={bonePickerRaycastEnabled}
          editableKeyPoseReferenceName={liveActorHiddenReferenceName}
          hoveredBonePickerJointIds={hoveredBonePickerJointIds}
          isMultiActorScene={isMultiActorScene}
          isPlaying={isPlaying}
          onBoneHover={handleBoneHover}
          onBoneSelect={handleBoneSelect}
          onDeselectReference={onDeselectReference}
          onSelectReference={onSelectReference}
          renderItem={renderItem}
          selectedBonePickerJointIds={selectedBonePickerJointIds}
          selectedReferenceName={selectedReferenceName}
        />
      ))}
    </>
  );
}

interface MotionEditorRouteSceneActorProps {
  activeKeyPoseReferenceName: string | null;
  bonePickerRaycastEnabled: boolean;
  editableKeyPoseReferenceName: string | null;
  hoveredBonePickerJointIds: readonly KeyPoseFkJointId[];
  isMultiActorScene: boolean;
  isPlaying: boolean;
  onBoneHover: (pick: MotionEditorBonePick | null) => void;
  onBoneSelect: (pick: MotionEditorBonePick) => boolean;
  onDeselectReference: () => void;
  onSelectReference: (referenceName: string) => void;
  renderItem: MotionEditorSceneActorRenderItem;
  selectedBonePickerJointIds: readonly KeyPoseFkJointId[];
  selectedReferenceName: string | null;
}

const MotionEditorRouteSceneActor = memo(function MotionEditorRouteSceneActor({
  activeKeyPoseReferenceName,
  bonePickerRaycastEnabled,
  editableKeyPoseReferenceName,
  hoveredBonePickerJointIds,
  isMultiActorScene,
  isPlaying,
  onBoneHover,
  onBoneSelect,
  onDeselectReference,
  onSelectReference,
  renderItem,
  selectedBonePickerJointIds,
  selectedReferenceName,
}: MotionEditorRouteSceneActorProps) {
  const store = useStore();
  const { actor, animation, characterKey } = renderItem;
  const characterSettings =
    store.state.characters.byReferenceName[actor.referenceName]
      ?.characterSettings ?? store.state.characters.characterSettings;
  const hideLiveActor = editableKeyPoseReferenceName === actor.referenceName;
  const characterRotation = getMotionEditorCharacterRotation(
    characterSettings.isLegacyCharacter,
  );

  return (
    <group>
      <group
        visible={!hideLiveActor}
        onClick={(event) => {
          if (!isMultiActorScene) return;
          event.stopPropagation();
          if (actor.referenceName === selectedReferenceName) {
            // Re-clicking the active actor deselects it, but that conflicts with
            // key pose editing interactions that raycast onto the character mesh.
            if (
              ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES ||
              editableKeyPoseReferenceName
            ) {
              return;
            }
            onDeselectReference();
          } else {
            onSelectReference(actor.referenceName);
          }
        }}
      >
        <Player.Character
          key={characterKey}
          actorId={actor.referenceName}
          characterID={actor.characterID}
          meshPath={actor.meshPath}
          meshFormat={actor.meshFormat}
          identityPath={actor.identityJsonURL}
          headBoneName={actor.headBoneName}
          animation={animation}
          rotationOffsets={actor.rotationOffsets}
          staticHands={characterSettings.staticHands}
          skinHex={characterSettings.skinHex}
          faceExpression={characterSettings.faceExpression}
          rotation={characterRotation}
        />
        <MotionEditorLiveBoneHoverPicker
          actorId={actor.referenceName}
          enabled={
            Boolean(activeKeyPoseReferenceName) &&
            !isPlaying &&
            !editableKeyPoseReferenceName &&
            actor.referenceName === activeKeyPoseReferenceName
          }
          hoveredJointIds={hoveredBonePickerJointIds}
          onBoneHover={onBoneHover}
          onBoneSelect={onBoneSelect}
          raycastEnabled={bonePickerRaycastEnabled}
          selectedJointIds={selectedBonePickerJointIds}
        />
      </group>
    </group>
  );
});

function getBonePickerJointIds<T extends string>(jointId: T | null) {
  return jointId ? [jointId] : [];
}
